import React, { useEffect, Fragment, useState } from "react";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Swal from "sweetalert2";
import { DeleteRadiologyTest, GetRadiologyTests } from "../../../../api/diagnosis/master/radiologyTestAPI";

const ListRadiologyTest = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const fetchRadiologyTests = async () => {
    dispatch({ type: "LOADER_START" });
    try {
      const response = await GetRadiologyTests();
      if (response.success) {
        showToast("success", response.message);
        const formattedData = response.data.map((item, index) => ({
          index: index + 1,
          id: item._id,
          name: item.name || "",
          shortName: item.shortName || "",
          category:
            item.investigationCategoryId?.name || "—",
          price: item.price ?? 0,
          intlPrice: item.intlPrice ?? 0,
          discountAllow: item.discountAllow,
          rateEditable: item.rateEditable,
          testType: item.testType || "",
          reportDays: item.reportDays ?? 0,
          active: item.active
        }));

        setData(formattedData);
        setFilteredData(formattedData);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message || "Error fetching data");
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  useEffect(() => {
    fetchRadiologyTests();
  }, []);

  useEffect(() => {
    const results = data.filter(item =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
    setFilteredData(results);
  }, [search, data]);

  const handleEditClick = id => {
    navigate(
      `/diagnosis/master/radiology-test/edit/${id}`
    );
  };

  const handleDeleteClick = async id => {
    Swal.fire({
      title: "Are you sure?",
      text: "This record will be deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    }).then(async result => {
      if (result.isConfirmed) {
        dispatch({ type: "LOADER_START" });
        try {
          const response = await DeleteRadiologyTest(id);
          if (response.success) {
            showToast("success", response.message);
            setData(prev => prev.filter(item => item.id !== id));
            setFilteredData(prev => prev.filter(item => item.id !== id));
          } else {
            showToast("error", response.message);
          }
        } catch (error) {
          showToast("error", error.message);
        } finally {
          dispatch({ type: "LOADER_STOP" });
        }
      }
    });
  };

  const columns = [
    { name: "S.No", selector: row => row.index, width: "10%" },
    { name: "Name", selector: row => row.name, sortable: true, width: "10%" },
    { name: "Short Name", selector: row => row.shortName, width: "10%" },
    { name: "Category", selector: row => row.category, width: "10%" },
    { name: "Price", selector: row => row.price, width: "10%" },
    { name: "Intl Price", selector: row => row.intlPrice, width: "10%" },
    { name: "Discount", selector: row => row.discountAllow, width: "10%" },
    { name: "Editable", selector: row => row.rateEditable, width: "10%" },
    { name: "Test Type", selector: row => row.testType, width: "10%" },
    { name: "Report Days", selector: row => row.reportDays, width: "10%" },
    { name: "Active", selector: row => row.active, width: "10%" },
    {
      name: "Actions",
      cell: row => (
        <div>
          <button
            className="btn btn-primary me-2"
            onClick={() => handleEditClick(row.id)}
            id={`edit-${row.id}`}
          >
            <i className="fa fa-pencil-square-o" />
          </button>
          <UncontrolledTooltip target={`edit-${row.id}`}>
            Edit
          </UncontrolledTooltip>

          <button
            className="btn btn-danger"
            onClick={() => handleDeleteClick(row.id)}
            id={`delete-${row.id}`}
          >
            <i className="fa fa-trash-o" />
          </button>
          <UncontrolledTooltip target={`delete-${row.id}`}>
            Delete
          </UncontrolledTooltip>
        </div>
      ),
      ignoreRowClick: true,
      button: true, 
      width: "15%"
    }
  ];

  const headers = [
    { label: "Name", key: "name" },
    { label: "Short Name", key: "shortName" },
    { label: "Category", key: "category" },
    { label: "Price", key: "price" },
    { label: "Intl Price", key: "intlPrice" },
    { label: "Discount Allowed", key: "discountAllow" },
    { label: "Rate Editable", key: "rateEditable" },
    { label: "Test Type", key: "testType" },
    { label: "Report Days", key: "reportDays" },
    { label: "Active", key: "active" }
  ];

  return (
    <Fragment>
      <Breadcrumb parent="Radiology" title="List Radiology Tests" />
      <div className="container-fluid">
        <div className="card">
          <div className="card-header">
            <div className="d-flex justify-content-between">
              <h5>Radiology Tests</h5>
              <Link
                to={`/diagnosis/master/radiology-test/add`}
              >
                <button className="btn btn-primary">
                  Create Radiology Test
                </button>
              </Link>
            </div>

            <div className="row mt-2">
              <div className="col-sm-6">
                <input
                  className="form-control"
                  placeholder="Search..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <div className="col-sm-6 text-end">
                <CSVLink
                  data={data}
                  headers={headers}
                  filename="radiology-tests.csv"
                  className="btn btn-primary"
                >
                  Export CSV
                </CSVLink>
              </div>
            </div>
          </div>

          <div className="card-body">
            <DataTable
              columns={columns}
              data={filteredData}
              pagination
              highlightOnHover
              responsive
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ListRadiologyTest;
